<template>
  <div class="tabs sanitas-body-medium-long">
    <nav class="tabs-navi-wrapper" data-track-as="tabs">
      <button
        v-show="isScrollableLeft"
        class="tabs-prev"
        role="button"
        data-track-text="previous"
        aria-label="previous"
        @click="scrollPrev"
      >
        <dotcom-icon name="chevron-right" art="light" rotation="180" />
      </button>
      <ul
        ref="tabsNavi"
        role="tabs-list"
        class="tabs-navi"
        aria-label="Entertainment"
      >
        <li
          v-for="tabItem of tabs"
          :key="tabItem.id"
          class="tabs-navi-item"
          :class="{ 'tabs-navi-item--selected': activeTabId === tabItem.id }"
          :data-track-text="tabItem.titleLabel"
        >
          <button
            :id="tabItem.id"
            role="tab"
            class="tabs-navi-item-trigger"
            :aria-selected="activeTabId === tabItem.id ? 'true' : 'false'"
            :aria-controls="`${tabItem.id}-tab`"
            @click="activateTab(tabItem.id)"
          >
            {{ tabItem.titleLabel }}
          </button>
        </li>
      </ul>
      <button
        v-show="isScrollableRight"
        class="tabs-next"
        role="button"
        data-track-text="next"
        aria-label="next"
        @click="scrollNext"
      >
        <dotcom-icon name="chevron-right" art="light" />
      </button>
    </nav>
    <div class="tabs-content">
      <slot />
    </div>
  </div>
</template>
<script>
import debounce from 'lodash/debounce'

export default {
  name: 'SanitasTabs',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },
  props: {
    activeTab: {
      type: String,
      default: '0',
    },
  },
  data() {
    return {
      tabs: [],
      tabsNavi: null,
      activeTabId: null,
      isScrollableLeft: false,
      isScrollableRight: false,
    }
  },
  async mounted() {
    this.tabsNavi = this.$refs.tabsNavi

    // Only consider correct children for creating tabs
    this.tabs = this.$children.filter(item => {
      return item.$vnode.componentOptions.tag === 'sanitas-tab-item'
    })

    // Set active tab either to one selected by the author or 0 as fallback
    let activeTabIndex = parseInt(this.activeTab, 10)
    // author works with 1 based index for convenience
    activeTabIndex--
    // check index is in range
    if (
      !activeTabIndex ||
      activeTabIndex < 0 ||
      activeTabIndex >= this.tabs.length
    ) {
      activeTabIndex = 0
    }
    this.activeTabId = this.tabs[activeTabIndex].id

    window.addEventListener('resize', this.updateNavigationVisibility)
    this.tabsNavi.addEventListener(
      'scroll',
      debounce(this.updateNavigationVisibility, 100),
    )

    await this.$nextTick()
    this.updateNavigationVisibility()
    this.autoHeightTeasers()

    // Only after a short timeout (10ms) the tabs navigation seem fully rendered
    // and ready to set correct scroll position for the active tab. It does not work with 0s!
    window.setTimeout(() => {
      this.activeTabInView()
    }, 10)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.updateNavigationVisibility)
    this.tabsNavi.removeEventListener(
      'scroll',
      debounce(this.updateNavigationVisibility, 100),
    )
  },
  methods: {
    async activateTab(id) {
      this.activeTabId = id
      await this.$nextTick()
      this.autoHeightTeasers()
    },
    updateNavigationVisibility() {
      this.isScrollable = this.tabsNavi.scrollWidth > this.tabsNavi.clientWidth
      if (this.isScrollable) {
        this.isScrollableRight =
          this.tabsNavi.scrollLeft + this.tabsNavi.offsetWidth <
          this.tabsNavi.scrollWidth
        this.isScrollableLeft = this.tabsNavi.scrollLeft > 0
      } else {
        this.isScrollableLeft = this.isScrollableRight = false
      }
    },
    scrollPrev() {
      this.tabsNavi.scrollBy({
        left: -this.tabsNavi.clientWidth / 4,
        behavior: 'smooth',
      })
    },
    scrollNext() {
      this.tabsNavi.scrollBy({
        left: this.tabsNavi.clientWidth / 4,
        behavior: 'smooth',
      })
    },
    autoHeightTeasers() {
      this.alignTeaserHeight('.sanitas-seal--type-card')
      this.alignTeaserHeight('.sanitas-product-box')
    },
    alignTeaserHeight(selector) {
      // Align teasers height within the tab
      const activeTabTeasers = this.$el.querySelectorAll(
        `.tab-item--active ${selector}`,
      )
      if (!activeTabTeasers.length) return
      let _height = 0
      for (const teaser of activeTabTeasers) {
        teaser.style.height = 'auto'
        if (_height < teaser.offsetHeight) {
          _height = teaser.offsetHeight
        }
      }
      for (const teaser of activeTabTeasers) {
        teaser.style.height = `${_height}px`
      }
    },
    activeTabInView() {
      // Ensure active tab is in view and return whether adjustment / scroll was needed
      const activeTab = this.$el.querySelector(`.tabs-navi-item--selected`)
      if (activeTab.offsetLeft > activeTab.parentNode.offsetWidth) {
        const prevButtonWidth = 26
        this.tabsNavi.scrollLeft = activeTab.offsetLeft - prevButtonWidth
      }
    },
  },
}
</script>
<style lang="postcss" scoped>
.tabs {
  @mixin antiBase;
  position: relative;
}

.tabs-navi-wrapper {
  display: none;
}

.tabs-navi {
  display: flex;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
}

.magazine-article .content-area ul.tabs-navi {
  /* FIXME: Cleanup magazine article base element stylings! */
  /* Needed to overwrite base styles from magazine article */
  margin-bottom: 0;
}

.tabs-next,
.tabs-prev {
  position: absolute;
  font-size: 16px;
  top: 0;
  z-index: 2;
  height: 48px;
  width: 26px;
  background: linear-gradient(
    to left,
    white,
    white 80%,
    rgba(255, 255, 255, 0.5) 95%,
    rgba(255, 255, 255, 0.1) 100%
  );
  cursor: pointer;

  /* reset */
  appearance: none;
  border: none;
}

.tabs-next {
  right: 0;
}

.tabs-prev {
  left: 0;
}

.tabs-navi-item {
  display: inline-block;
  position: relative;
  height: 48px;
  min-width: 190px;
  max-width: 240px;
  flex: 1 0 auto;
  border-bottom: 1px solid var(--c-secondary-neutral-2);
}

.tabs-navi-item--selected .tabs-navi-item-trigger::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  background-color: var(--c-primary-neutral-3);
  height: 2px;
}

.tabs-navi-item + .tabs-navi-item {
  padding-left: var(--spacing-2);
}

.tabs-navi-item-trigger {
  display: flex;
  padding: var(--spacing-1) var(--spacing-4);
  height: 100%;
  width: 100%;
  white-space: normal;
  text-align: left;
  background-color: var(--c-secondary-neutral-3);
  border: 1px solid transparent;
  cursor: pointer;

  /* reset */
  appearance: none;

  /* Needed to overwrite base styles from magazine article */
  font-size: 14px;
  line-height: 20px;
  color: var(--c-primary-neutral-1);

  &:hover {
    color: var(--c-primary-color-3);
  }

  &:active {
    color: var(--c-primary-color-2);
  }
}

.tabs-navi-item--selected .tabs-navi-item-trigger {
  position: relative;
  z-index: 1;
  font-weight: bold;
  color: var(--c-primary-color-2);
  background-color: var(--c-primary-neutral-3);
  border: 1px solid var(--c-secondary-neutral-2);
}

.container-wrapper.warm-gray {
  .tabs-navi-item {
    border-bottom-color: var(--c-primary-neutral-1);
  }

  .tabs-navi-item-trigger {
    border: 1px solid var(--c-primary-neutral-1);
    border-bottom: none;
    background-color: var(--c-primary-neutral-2);
  }

  .tabs-navi-item--selected .tabs-navi-item-trigger {
    border-color: var(--c-primary-neutral-1);
    background-color: var(--c-primary-neutral-3);
  }
}

.tabs-content {
  margin-bottom: var(--spacing-8);
}

@media (--md) {
  .tabs-navi-wrapper {
    display: block;
  }

  .tabs-content {
    margin-bottom: 0;
    padding: var(--spacing-8) 0;
    background-color: var(--c-primary-neutral-3);
  }
}
</style>
